import { useEffect } from "react";

// Slow right-to-left auto-scroll for a horizontal row.
// Seamless infinite loop requires the caller to duplicate its content
// (render items twice) so scrollLeft can wrap at scrollWidth/2 invisibly.
// Pauses on touch/hover/wheel, resumes ~2.5s after the last interaction.
export const useAutoScrollX = (ref, enabled, speed = 0.4, reverse = false) => {
  useEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    if (!el) return;
    let raf, paused = false, resumeT, pos = reverse ? el.scrollWidth / 2 : el.scrollLeft;
    if (reverse && el.scrollWidth > el.clientWidth) el.scrollLeft = pos;
    const step = () => {
      if (!paused && el.scrollWidth > el.clientWidth) {
        const half = el.scrollWidth / 2;
        pos += reverse ? -speed : speed;
        if (pos >= half) pos -= half;
        if (pos < 0) pos += half;
        el.scrollLeft = pos;
      }
      raf = requestAnimationFrame(step);
    };
    const pause = () => { paused = true; clearTimeout(resumeT); };
    const resume = () => { clearTimeout(resumeT); resumeT = setTimeout(() => { pos = el.scrollLeft; paused = false; }, 2500); };
    el.addEventListener("touchstart", pause, { passive: true });
    el.addEventListener("touchend", resume, { passive: true });
    el.addEventListener("mouseenter", pause);
    el.addEventListener("mouseleave", resume);
    el.addEventListener("wheel", () => { pause(); resume(); }, { passive: true });
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf); clearTimeout(resumeT);
      el.removeEventListener("touchstart", pause);
      el.removeEventListener("touchend", resume);
      el.removeEventListener("mouseenter", pause);
      el.removeEventListener("mouseleave", resume);
    };
  }, [enabled, ref, speed, reverse]);
};
